import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

/**
 * RTK Query slice for the LISTING ACTIVITY FEED — REP-205 (Listing/Push
 * pillar). Powers the listing page's "Activity" tab.
 *
 * The feed is a time-ordered UNION across three families of Activity rows,
 * all keyed off the listing's shared property:
 *   (a) the listing / seller-rep project itself (blasts, pushes, invites,
 *       listing-sheet publish, listing-interest added)
 *   (b) the listing's property (tours, LOIs, views, interest)
 *   (c) related deals that touch the same property (best-effort)
 *
 * Endpoint (mounted under `/listings`, FE `/api` stripped upstream):
 *   GET /api/listings/:id/activity?limit=&cursor=
 *
 * `:id` = the lister's seller-rep Project id (`listingProjectId`).
 *
 * Keep the types below in sync with
 * `repsity-service/src/services/listingActivity.service.ts`.
 */

/**
 * Coarse UI bucket for icon/color grouping. Mirrors the backend
 * `ListingActivityItem.group` union.
 */
export type ListingActivityGroup =
  | "outreach"
  | "inbound"
  | "listing"
  | "tour"
  | "document"
  | "other";

/** A clean, presentation-ready feed item (1:1 with the backend DTO). */
export interface ListingActivityItem {
  id: string;
  /** Raw ActivityType enum string (e.g. "BLAST_SENT"). */
  type: string;
  /** Human verb phrase, e.g. "sent a marketing blast". */
  verb: string;
  /** Full one-line text, e.g. "Jane Doe sent a marketing blast". */
  text: string;
  actor: { id: string; name: string };
  /** What the activity is about (entity / parent), best-effort. */
  target: { type: string; id: string; name: string | null } | null;
  /** ISO timestamp. */
  when: string;
  group: ListingActivityGroup;
}

export interface ListingActivityResult {
  data: ListingActivityItem[];
  nextCursor: string | null;
  total: number;
}

export interface GetListingActivityArg {
  listingProjectId: string;
  limit?: number;
  /** Last item id from the prior page (createdAt-desc cursor). */
  cursor?: string;
}

export const listingActivityApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "listingActivityApi",
    // :id = the lister's seller-rep Project id (listingProjectId).
    baseUrl: "/api/listings",
  }),
  reducerPath: "listingActivityApi",
  tagTypes: ["ListingActivity"],
  endpoints: (builder) => ({
    /**
     * GET /api/listings/:id/activity
     *
     * Time-ordered union activity feed for one listing. Cursor-paginated in
     * lockstep with createdAt-desc (pass the prior page's last item id as
     * `cursor`). Tagged by listingProjectId so outreach/inbound mutations can
     * invalidate it.
     */
    getListingActivity: builder.query<
      { data: ListingActivityResult },
      GetListingActivityArg
    >({
      query: ({ listingProjectId, limit, cursor }) => {
        const params = new URLSearchParams();
        if (limit != null) params.set("limit", String(limit));
        if (cursor) params.set("cursor", cursor);
        const qs = params.toString();
        return `/${listingProjectId}/activity${qs ? `?${qs}` : ""}`;
      },
      providesTags: (_result, _error, { listingProjectId }) => [
        { type: "ListingActivity", id: listingProjectId },
      ],
    }),
  }),
});

export const { useGetListingActivityQuery, useLazyGetListingActivityQuery } =
  listingActivityApi;
